import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'

import Button from '@/components/ui/Button'

interface Props {
  name: string
  email: string
  invitationLink: string
  expiresAt: string | Date
  /** Tytuł — default „Zaproszenie wysłane", dla resend „Nowe zaproszenie wysłane". */
  title?: string
  onClose: () => void
}

export default function InvitationModal({
  name,
  email,
  invitationLink,
  expiresAt,
  title = 'Zaproszenie wysłane',
  onClose,
}: Props) {
  const [copied, setCopied] = useState(false)
  const expiresAtDate =
    typeof expiresAt === 'string' ? new Date(expiresAt) : expiresAt

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(invitationLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // brak uprawnień — user widzi link i tak
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="invitation-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg-canvas/80 backdrop-blur-sm p-4"
    >
      <div className="rounded-card border border-border-subtle bg-bg-elevated p-6 max-w-md w-full">
        <h2 id="invitation-modal-title" className="text-xl font-semibold mb-2">
          {title}
        </h2>
        <p className="text-sm text-text-muted mb-4">
          Email z zaproszeniem został wysłany do <strong>{name}</strong> na{' '}
          <strong className="font-mono text-xs">{email}</strong>. Jeśli email
          nie dojdzie, link poniżej można wysłać SMSem lub pokazać osobiście.
        </p>

        <div className="rounded-card bg-brand-dark text-bg-elevated font-mono text-xs p-3 mb-2 break-all select-all">
          {invitationLink}
        </div>
        <p className="text-xs text-text-faint mb-4">
          Link ważny do{' '}
          <strong className="text-text-muted">
            {format(expiresAtDate, "d MMM yyyy, HH:mm", { locale: pl })}
          </strong>
        </p>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={handleCopy}
            className="flex-1"
            type="button"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4" aria-hidden /> Skopiowane
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" aria-hidden /> Kopiuj link
              </>
            )}
          </Button>
          <Button onClick={onClose} className="flex-1" type="button">
            Zamknij
          </Button>
        </div>
      </div>
    </div>
  )
}
